import { useState, useEffect } from 'react';
import { AlertTriangle, Clock, TrendingDown, Loader2, RefreshCw } from 'lucide-react';
import { dashboardApi } from '@/api/client';
import type { Alert } from '@/types';
import { format, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import clsx from 'clsx';

export function AlertsPage() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAlerts = async () => {
    setIsLoading(true);
    const response = await dashboardApi.alerts();
    if (response.success && response.data) {
      setAlerts(response.data);
      setError(null);
    } else {
      setError(response.error || 'アラートの取得に失敗しました');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchAlerts();
  }, []);

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'expired': return AlertTriangle;
      case 'expiring': return Clock;
      case 'low_stock': return TrendingDown;
      default: return AlertTriangle;
    }
  };

  const getAlertColor = (severity: string) => {
    switch (severity) {
      case 'error': return 'border-red-200 bg-red-50';
      case 'warning': return 'border-yellow-200 bg-yellow-50';
      default: return 'border-blue-200 bg-blue-50';
    }
  };

  const getAlertTextColor = (severity: string) => {
    switch (severity) {
      case 'error': return 'text-red-800';
      case 'warning': return 'text-yellow-800';
      default: return 'text-blue-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded-lg">
        {error}
        <button onClick={fetchAlerts} className="ml-4 underline">
          再読み込み
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">アラート</h1>
        <button onClick={fetchAlerts} className="btn-ghost flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          更新
        </button>
      </div>

      {alerts.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-lg font-medium text-gray-900 mb-2">
            アラートはありません
          </h2>
          <p className="text-gray-500">
            在庫状況は正常です
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {alerts.map((alert, index) => {
            const Icon = getAlertIcon(alert.type);
            return (
              <div
                key={index}
                className={clsx('card border', getAlertColor(alert.severity))}
              >
                <div className="p-4 border-b border-inherit">
                  <div className="flex items-center gap-3">
                    <Icon className={clsx('h-5 w-5', getAlertTextColor(alert.severity))} />
                    <h2 className={clsx('font-semibold', getAlertTextColor(alert.severity))}>
                      {alert.message}
                    </h2>
                  </div>
                </div>
                <div className="p-4">
                  {alert.type === 'expired' && (
                    <ExpiredItemsList items={alert.items as ExpiredItem[]} />
                  )}
                  {alert.type === 'expiring' && (
                    <ExpiringItemsList items={alert.items as ExpiringItem[]} />
                  )}
                  {alert.type === 'low_stock' && (
                    <LowStockItemsList items={alert.items as LowStockItem[]} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface ExpiredItem {
  id: number;
  product_name: string;
  product_unit: string;
  lot_number: string;
  quantity: number;
  expiry_date: string;
  days_expired: number;
}

function ExpiredItemsList({ items }: { items: ExpiredItem[] }) {
  return (
    <ul className="divide-y divide-red-200">
      {items.map((item) => (
        <li key={item.id} className="py-3 flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">{item.product_name}</p>
            <p className="text-sm text-gray-500">
              ロット: {item.lot_number} / {item.quantity}{item.product_unit}
            </p>
          </div>
          <div className="text-right">
            <span className="badge-danger">
              {item.days_expired}日超過
            </span>
            <p className="text-xs text-gray-500 mt-1">
              {format(parseISO(item.expiry_date), 'yyyy/MM/dd', { locale: ja })}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

interface ExpiringItem {
  id: number;
  product_name: string;
  product_unit: string;
  lot_number: string;
  quantity: number;
  expiry_date: string;
  days_until_expiry: number;
  expiry_alert_days: number;
}

function ExpiringItemsList({ items }: { items: ExpiringItem[] }) {
  return (
    <ul className="divide-y divide-yellow-200">
      {items.map((item) => (
        <li key={item.id} className="py-3 flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">{item.product_name}</p>
            <p className="text-sm text-gray-500">
              ロット: {item.lot_number} / {item.quantity}{item.product_unit}
            </p>
          </div>
          <div className="text-right">
            <span className={clsx(
              'badge',
              item.days_until_expiry <= 3 ? 'badge-danger' : 'badge-warning'
            )}>
              あと{item.days_until_expiry}日
            </span>
            <p className="text-xs text-gray-500 mt-1">
              {format(parseISO(item.expiry_date), 'yyyy/MM/dd', { locale: ja })}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

interface LowStockItem {
  id: number;
  name: string;
  unit: string;
  min_stock: number;
  current_stock: number;
}

function LowStockItemsList({ items }: { items: LowStockItem[] }) {
  return (
    <ul className="divide-y divide-yellow-200">
      {items.map((item) => (
        <li key={item.id} className="py-3 flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">{item.name}</p>
            <p className="text-sm text-gray-500">
              最小在庫: {item.min_stock}{item.unit}
            </p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-red-600">
              {item.current_stock}{item.unit}
            </p>
            <p className="text-xs text-red-500">
              不足: {item.min_stock - item.current_stock}{item.unit}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}
